import { useNotifications } from "@/context/NotificationContext";
import { useAuth } from "@/hooks/useAuth";
import { useSocket } from "@/hooks/useSocket";
import { useTheme } from "@/hooks/useTheme";
import { getSecureUser, getAuthToken } from "@/utils/secureStorage";
import { Notification } from "@/types";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

// Pages
import CompanyAdminDashboard from "@/pages/admin/CompanyAdminDashboard";
import Departments from "@/pages/admin/Departments";
import Employees from "@/pages/admin/Employees";
import ManageCourses from "@/pages/admin/ManageCourses";
import Analytics from "@/pages/Analytics";
import ForgotPasswordCompany from "@/pages/auth/company/ForgotPassword";
import ResetPasswordCompany from "@/pages/auth/company/ResetPassword";
import EmailVerification from "@/pages/auth/EmailVerification";
import Login from "@/pages/auth/Login";
import OtpVerification from "@/pages/auth/OtpVerification";
import Register from "@/pages/auth/Register";
import RegisterCompany from "@/pages/auth/RegisterCompany";
import ForgotPasswordUser from "@/pages/auth/user/ForgotPassword";
import ResetPasswordUser from "@/pages/auth/user/ResetPassword";
import VerificationFailed from "@/pages/auth/VerificationFailed";

import CourseDetails from "@/pages/CourseDetails";
import Courses from "@/pages/Courses";
import Dashboard from "@/pages/Dashboard";
import LandingPage from "@/pages/LandingPage";
import Leaderboard from "@/pages/Leaderboard";
import Messaging from "@/pages/Messaging";
import NotFound from "@/pages/NotFound";
import Policies from "@/pages/Policies";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import FAQ from "@/pages/FAQ";
import OnboardingTutorials from "@/pages/OnboardingTutorials";

// Layouts
import AuthLayout from "@/layouts/AuthLayout";
import MainLayout from "@/layouts/MainLayout";

// Components
// import AiAssistant from "@/components/assistant/AiAssistant";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

const AppContent = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const { currentUser, currentUserLoading, isInitialized } = useAuth();
  const { addNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(true);

  // Get backend URL from env or fallback
  const backendUrl =
    import.meta.env.VITE_BACKEND_ENDPOINT ||
    "https://api.production.gnzabe.com";

  // Memoized handler for new notifications
  const handleNewNotification = useCallback(
    (notification: Notification) => {
      addNotification(notification);
    },
    [addNotification]
  );

  // Connect to socket if user is logged in
  useSocket({
    serverUrl: backendUrl,
    userId: currentUser?.id || currentUser?._id || "",
    onNotification: handleNewNotification,
  });

  // Apply theme class to body
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  // Set HTML lang attribute based on current language
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Simulate loading state for the app initialization
  useEffect(() => {
    if (isInitialized) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500); // Reduced from 1000ms to 500ms
      return () => clearTimeout(timer);
    }
  }, [isInitialized]);

  // Skip loading for public routes
  const isPublicRoute = [
    "/",
    "/login",
    "/register",
    "/register-company",
    "/forgot-password",
    "/reset-password",
    "/verification-failed",
  ].includes(location.pathname);

  if ((isLoading || currentUserLoading) && !isPublicRoute) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="h-16 w-16 animate-pulse-slow rounded-full bg-primary-500"></div>
          <h1 className="text-2xl font-bold text-primary-800 dark:text-primary-200">
            Loading...
          </h1>
        </div>
      </div>
    );
  }

  // Redirect to appropriate dashboard based on user role
  const getDashboardRoute = () => {
    if (!currentUser) return "/login";

    switch (currentUser.role) {
      case "company_admin":
        return "/admin/company";
      case "employee":
        return "/employee/dashboard";
      case "department_admin":
        return "/department-admin/dashboard";
      default:
        return "/employee/dashboard";
    }
  };

  const Unauthorized = () => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4 text-center dark:bg-gray-900">
      <h1 className="text-6xl font-bold text-red-500 dark:text-red-400 mb-4">
        401
      </h1>
      <h2 className="mb-4 text-2xl font-bold text-gray-900 dark:text-white">
        Unauthorized
      </h2>
      <p className="mb-8 max-w-md text-lg text-gray-600 dark:text-gray-300">
        You do not have permission to access this page.
      </p>
      <a href="/" className="text-blue-600 hover:underline">
        Go Home
      </a>
    </div>
  );

  return (
    <>
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/tutorials" element={<OnboardingTutorials />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-company" element={<RegisterCompany />} />
          <Route path="/user/reset-password" element={<ResetPasswordUser />} />
          <Route
            path="/user/forgot-password"
            element={<ForgotPasswordUser />}
          />
          <Route
            path="/company/forgot-password"
            element={<ForgotPasswordCompany />}
          />
          <Route
            path="/company/reset-password"
            element={<ResetPasswordCompany />}
          />
          <Route path="/verify-email" element={<EmailVerification />} />
        </Route>

        {/* Standalone verification routes */}
        <Route path="/verify-otp" element={<OtpVerification />} />
        <Route path="/verification-failed" element={<VerificationFailed />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Redirect to appropriate dashboard */}
          <Route
            path="/dashboard"
            element={<Navigate to={getDashboardRoute()} replace />}
          />

          {/* Company Admin routes */}
          <Route
            path="/admin/company"
            element={
              <ProtectedRoute requiredRole="company_admin">
                <CompanyAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <ProtectedRoute>
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute requiredRole="company_admin">
                <Departments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manage-courses"
            element={
              <ProtectedRoute
                requiredRole={["department_admin", "departmentAdmin"]}
              >
                <ManageCourses />
              </ProtectedRoute>
            }
          />
          {/* Test route for debugging */}
          <Route path="/test-manage-courses" element={<ManageCourses />} />
          {/* Simple test route without any protection */}
          <Route
            path="/test-simple"
            element={
              <div className="p-8">
                <h1>Simple Test Page</h1>
                <p>If you can see this, routing is working.</p>
                <p>User role: {JSON.stringify(getSecureUser())}</p>
                <p>Token exists: {!!getAuthToken()}</p>
              </div>
            }
          />

          {/* Employee routes */}
          <Route
            path="/employee/dashboard"
            element={
              <ProtectedRoute requiredRole="employee">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/department-admin/dashboard"
            element={
              <ProtectedRoute requiredRole="department_admin">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <Courses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:id"
            element={
              <ProtectedRoute>
                <CourseDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <Leaderboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/policies"
            element={
              <ProtectedRoute>
                <Policies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messaging"
            element={
              <ProtectedRoute>
                <Messaging />
              </ProtectedRoute>
            }
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
        </Route>

        {/* 404 route */}
        <Route path="*" element={<NotFound />} />

        {/* Unauthorized route */}
        <Route path="/unauthorized" element={<Unauthorized />} />
      </Routes>
    </>
  );
};

export default AppContent;
